import get from 'lodash/get';
import { matchPath } from 'react-router-dom';
import { createSelector } from 'reselect';

import { pathname } from '@savgroup-front-common/core/domains/selectors/router';
import { OrderDetails } from '@savgroup-front-common/types';

import { ROUTES } from '../../constants/routing';
import { Selectors as AccessSelectors } from '../access';
import { ControlRootState } from '../ControlRootState';
import { currentFileSelector } from '../fileInfo/fileDetails';

export const ordersState = (state: ControlRootState) => state.orders;

export const selectDetailedOrders = createSelector(
  [ordersState],
  (orders) => orders.detailedOrders,
);
export const getDetailedOrderByOrderId = createSelector(
  [
    selectDetailedOrders,
    (_state: ControlRootState, { orderId }: { orderId?: string }) => orderId,
  ],
  (detailedOrdersState, orderId) => {
    if (!orderId) {
      return undefined;
    }

    return (
      detailedOrdersState[orderId]?.value || {
        products: [],
      }
    );
  },
);

export const selectOrderById = createSelector(
  [
    selectDetailedOrders,
    (_: ControlRootState, { orderId }: { orderId?: string }) => orderId,
  ],
  (detailedOrders, orderId): OrderDetails | undefined =>
    detailedOrders && orderId ? detailedOrders[orderId]?.value : undefined,
);
export const currentOrderIdSelector = createSelector(
  [pathname, currentFileSelector],
  (path, currentFile) => {
    const options = matchPath(ROUTES.ORDERS, path);
    const orderId = get(options, 'params.orderId');

    if (!orderId && currentFile) {
      return currentFile.orderId;
    }

    return orderId ?? '';
  },
);

export const getBackOfficeLinkWithSubrouteByOrderId = createSelector(
  [
    AccessSelectors.backOfficeLinkValue,
    (_state: any, orderId: string) => orderId,
    (_state, _orderId: string, subroute = '') => subroute,
  ],
  (backOfficeLink, orderId, subroute) => {
    if (backOfficeLink) {
      return backOfficeLink[orderId]?.[subroute];
    }

    return undefined;
  },
);

export const currentOrderSelector = createSelector(
  [selectDetailedOrders, currentOrderIdSelector],
  (detailedOrders, currentOrderId) => detailedOrders[currentOrderId],
);

export const currentOrderOwnerIdSelector = createSelector(
  [currentOrderSelector],
  (currentOrder) => (currentOrder ? currentOrder.value?.ownerId : undefined),
);
export const currentOrderModelIdSelector = createSelector(
  [currentOrderSelector],
  (currentOrder) =>
    currentOrder
      ? (currentOrder.value?.products || []).map((product) => product.modelId)
      : [],
);
